import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { TextField, GoogleAddressField, Select, FieldData, NumbersOnly } from 'vopay';

import { PaymentFieldDefault } from './types';

export function useContactAddressFields(
  Country: string,
  shouldShowAddressFields: boolean,
  addressFieldsRequired: boolean,
): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  return useMemo(() => {
    const countryOptions = [
      { label: t('countryOptions.CA'), value: 'CA' },
      { label: t('countryOptions.US'), value: 'US' },
    ].filter((o) => o.value === Country);

    function getRequiredInputState() {
      return addressFieldsRequired ? 'required' : 'optional';
    }

    return [
      {
        label: () => t('Fields.recipientPhoneNumber.label'),
        fieldName: 'RecipientPhoneNumber',
        getInputState: () => (Country === 'US' && addressFieldsRequired ? 'required' : 'optional'),
        showField: () => shouldShowAddressFields && Country === 'US',
        serializeField: (values) => NumbersOnly(values.RecipientPhoneNumber),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
        },
        validate: () => (Country === 'US' && addressFieldsRequired ? Yup.string().required() : Yup.string()),
      },
      {
        label: () => t('Fields.address.label'),
        fieldName: 'Address1',
        getInputState: getRequiredInputState,
        showField: () => shouldShowAddressFields,
        serializeField: (values) => values.Address1,
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <GoogleAddressField
              variant="outlineShadow"
              ISOCountry={[Country]}
              onGoogleAddressSelect={(addressComponents) => {
                ctx.setFieldValue('AddressLine1', addressComponents.address);
                ctx.setFieldValue('City', addressComponents.city);
                ctx.setFieldValue('Province', addressComponents.province);
                ctx.setFieldValue('PostalCode', addressComponents.postalcode);
                ctx.setFieldValue('Country', addressComponents.country);
                ctx.setFieldTouched('AddressLine1', true);
                ctx.setFieldTouched('City', true);
                ctx.setFieldTouched('Province', true);
                ctx.setFieldTouched('PostalCode', true);
                ctx.setFieldTouched('Country', true);
              }}
              placeholder={t('Fields.address.placeholder')}
              {...fieldData}
            />
          );
        },
        validate: () => (addressFieldsRequired ? Yup.string().required() : Yup.string()),
      },
      {
        label: () => t('Fields.address2.label'),
        fieldName: 'Address2',
        getInputState: () => 'optional',
        showField: () => shouldShowAddressFields,
        serializeField: (values) => values.Address2,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder={t('Fields.address2.placeholder')} {...fieldData} />;
        },
      },
      {
        label: () => t('Fields.city.label'),
        fieldName: 'City',
        getInputState: getRequiredInputState,
        showField: () => shouldShowAddressFields,
        serializeField: (values) => values.City,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder={t('Fields.city.placeholder')} {...fieldData} />;
        },
        validate: () => (addressFieldsRequired ? Yup.string().required() : Yup.string()),
      },
      {
        label: () => t('Fields.province.label'),
        fieldName: 'Province',
        getInputState: getRequiredInputState,
        showField: () => shouldShowAddressFields,
        serializeField: (values) => values.Province,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder={t('Fields.province.placeholder')} {...fieldData} />;
        },
        validate: () => (addressFieldsRequired ? Yup.string().required() : Yup.string()),
      },
      {
        label: () => t('Fields.country.label'),
        fieldName: 'Country',
        getInputState: getRequiredInputState,
        showField: () => shouldShowAddressFields,
        serializeField: (values) => values.Country,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select version="2" placeholder={t('Fields.country.placeholder')} options={countryOptions} {...fieldData} />
          );
        },
        validate: () => (addressFieldsRequired ? Yup.string().required() : Yup.string()),
      },
      {
        label: () => t('Fields.postalCode.label'),
        fieldName: 'PostalCode',
        getInputState: getRequiredInputState,
        showField: () => shouldShowAddressFields,
        serializeField: (values) => values.PostalCode,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder={t('Fields.postalCode.placeholder')} {...fieldData} />;
        },
        validate: () => (addressFieldsRequired ? Yup.string().required() : Yup.string()),
      },
    ];
  }, [Country, shouldShowAddressFields, addressFieldsRequired]);
}
